import type { LatLng } from './types';

export type WeatherSnapshot = {
  tempC: number;
  windKmh: number;
  /** Meteorological degrees: where the wind comes FROM (0 = N, 90 = E). */
  windFromDeg: number;
  fetchedAtMs: number;
  location: LatLng;
};

type OpenMeteoCurrentResponse = {
  current?: {
    temperature_2m?: number;
    wind_speed_10m?: number;
    wind_direction_10m?: number;
  };
};

const ENDPOINT = 'https://api.open-meteo.com/v1/forecast';
/** Refresh when the focus point moves this far (map pan / destination). */
const MOVE_REFRESH_M = 1_500;

export function weatherNeedsRefresh(
  last: WeatherSnapshot | null,
  here: LatLng,
  nowMs: number,
  intervalMs: number,
  moveRefreshM: number = MOVE_REFRESH_M,
): boolean {
  if (!last) return true;
  if (nowMs - last.fetchedAtMs >= intervalMs) return true;
  const dlat = (here.lat - last.location.lat) * 111_320;
  const midLat = ((here.lat + last.location.lat) / 2) * (Math.PI / 180);
  const dlng = (here.lng - last.location.lng) * 111_320 * Math.cos(midLat);
  return Math.hypot(dlat, dlng) >= moveRefreshM;
}

export async function fetchCurrentWeather(
  here: LatLng,
  signal?: AbortSignal,
): Promise<WeatherSnapshot> {
  const url = new URL(ENDPOINT);
  url.searchParams.set('latitude', here.lat.toFixed(4));
  url.searchParams.set('longitude', here.lng.toFixed(4));
  url.searchParams.set(
    'current',
    'temperature_2m,wind_speed_10m,wind_direction_10m',
  );
  url.searchParams.set('temperature_unit', 'celsius');
  url.searchParams.set('wind_speed_unit', 'kmh');
  url.searchParams.set('timezone', 'auto');
  url.searchParams.set('forecast_days', '1');

  const res = await fetch(url.toString(), { signal });
  if (!res.ok) {
    throw new Error(`Weather HTTP ${String(res.status)}`);
  }
  const data = (await res.json()) as OpenMeteoCurrentResponse;
  const cur = data.current;
  const tempC = Number(cur?.temperature_2m);
  const windKmh = Number(cur?.wind_speed_10m);
  const windFromDeg = Number(cur?.wind_direction_10m);
  if (
    !Number.isFinite(tempC) ||
    !Number.isFinite(windKmh) ||
    !Number.isFinite(windFromDeg)
  ) {
    throw new Error('Weather payload incomplete.');
  }
  return {
    tempC,
    windKmh,
    windFromDeg,
    fetchedAtMs: Date.now(),
    location: { lat: here.lat, lng: here.lng },
  };
}

/** Degrees the wind blows TOWARD (for an arrow on screen). */
export function windTowardDeg(windFromDeg: number): number {
  return (((windFromDeg + 180) % 360) + 360) % 360;
}

export function formatTempC(tempC: number): string {
  const rounded = Math.round(tempC);
  return `${rounded > 0 ? '+' : ''}${String(rounded)}°`;
}

export function formatWindKmh(windKmh: number): string {
  return `${String(Math.round(windKmh))} km/h`;
}
